import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { CLIENT_NAV } from "@/lib/client-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { Library } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useClientTier } from "@/hooks/useClientTier";
import { CareerInviteCard } from "@/components/conversion/CareerInviteCard";

export const Route = createFileRoute("/client/library")({
  head: () => ({ meta: [{ title: "Library — Command Center" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="client">
      <PortalShell title="Family Command Center" nav={CLIENT_NAV} tourKey="client_tour">
        <LibraryPage />
      </PortalShell>
    </ProtectedRoute>
  ),
});

function LibraryPage() {
  const { user } = useAuth();
  const { isLegacy } = useClientTier();
  const [orders, setOrders] = useState<any[]>([]);
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase.from("orders").select("id, created_at, total_cents, fulfillment_status, order_items(product_title_snapshot)").eq("user_id", user.id).eq("status", "paid").order("created_at", { ascending: false });
      setOrders(data ?? []);
    })();
  }, [user?.id]);

  return (
    <PortalPage eyebrow="Currency Thoughts Store" title="Your library" intro="Books, courses, and guides you've purchased from the Currency Thoughts Store.">
      {isLegacy && (
        <div className="mb-6">
          <CareerInviteCard source="library" variant="banner" />
        </div>
      )}
      {orders.length === 0 ? (
        <div className="bg-white border border-bronze-600/20 rounded-[4px] p-10 text-center shadow-soft">
          <Library className="w-10 h-10 text-bronze-600 mx-auto" />
          <h3 className="mt-4 font-display text-navy-800 text-[22px]">Your library is empty</h3>
          <p className="mt-2 font-sans text-[14px] text-charcoal-500">Browse the Store to add your first title.</p>
          <Link to="/store" className="inline-block mt-4 px-5 py-2.5 bg-navy-800 text-cream-50 rounded-[4px] font-sans text-[13px] uppercase tracking-wider">Visit the Store</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o: any) => (
            <div key={o.id} className="bg-white border border-bronze-600/20 rounded-[4px] p-5 shadow-soft flex items-center justify-between">
              <div>
                <p className="font-display text-navy-800">{o.order_items?.map((i: any) => i.product_title_snapshot).join(", ") || "Library purchase"}</p>
                <p className="font-sans text-[12px] text-charcoal-500">{new Date(o.created_at).toLocaleDateString()} · ${(o.total_cents / 100).toFixed(2)}</p>
              </div>
              <Link to="/store/library" className="text-bronze-600 font-sans text-[12px] uppercase tracking-wider">View</Link>
            </div>
          ))}
        </div>
      )}
    </PortalPage>
  );
}
